import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import classNames from "classnames";
import { RxDashboard } from "react-icons/rx";
import { MdOutlineAttachMoney } from "react-icons/md";
import { FiLogOut, FiChevronRight, FiHome, FiUsers, FiCalendar, FiSettings, FiShield } from "react-icons/fi";
import { FaTree, FaBuilding } from "react-icons/fa";
import { AiOutlineMenuFold, AiOutlineMenuUnfold } from "react-icons/ai";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { IoGitMergeOutline, IoLeafOutline } from "react-icons/io5";
import { GrUserManager, GrTask } from "react-icons/gr";
import { BiTask } from "react-icons/bi";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";
import { TbMapPin } from "react-icons/tb";
import { BsChevronDown } from "react-icons/bs";

// MenuItemTooltip component
const MenuItemTooltip = ({ children, title, isCollapsed }) => (
  <div className="relative group">
    {children}
    {isCollapsed && (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 0, x: -10 }}
        whileHover={{ opacity: 1, x: 0 }}
        className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-xl whitespace-nowrap z-50 border border-cyan-500/30 hidden group-hover:block"
        style={{ 
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)',
          backdropFilter: 'blur(8px)',
          background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)'
        }}
      >
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse"></span>
          {title}
        </div>
        <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-2 border-b-2 border-l-0 border-r-8 border-r-gray-900 border-t-transparent border-b-transparent"></div>
      </motion.div>
    )}
  </div>
);

// SidebarGroup component
const SidebarGroup = ({
  title,
  icon,
  menuKey,
  isCollapsed,
  openMenus,
  toggleMenu,
  children,
  badgeCount,
  isMobile = false
}) => {
  const isOpen = openMenus?.[menuKey] || false;

  return (
    <div className="mb-1.5">
      {isCollapsed ? (
        <MenuItemTooltip title={title} isCollapsed={isCollapsed}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => toggleMenu(menuKey)}
            className="flex items-center justify-center py-3 px-2 hover:bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl transition-all duration-200 w-full relative group"
          >
            <div className="relative">
              <motion.div
                whileHover={{ rotate: [0, -10, 10, -5, 0] }}
                transition={{ duration: 0.5 }}
              >
                {icon}
              </motion.div>
              {badgeCount && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center shadow-lg"
                >
                  {badgeCount}
                </motion.span>
              )}
            </div>
          </motion.button>
        </MenuItemTooltip>
      ) : (
        <div>
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => toggleMenu(menuKey)}
            className={classNames(
              "flex items-center justify-between w-full py-3 px-3 rounded-xl transition-all duration-200 group border",
              {
                "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-cyan-500/20": isOpen,
                "hover:bg-gradient-to-r hover:from-cyan-500/20 hover:to-blue-500/20 border-transparent hover:border-cyan-500/20": !isOpen
              }
            )}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, -5, 0] }}
                  transition={{ duration: 0.5 }}
                  className={classNames("transition-colors duration-200", {
                    "text-cyan-400": isOpen,
                    "text-gray-300": !isOpen
                  })}
                >
                  {icon}
                </motion.div>
                {badgeCount && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center shadow-md"
                  >
                    {badgeCount}
                  </motion.span>
                )}
              </div>
              <span className={classNames("font-medium transition-colors duration-200", {
                "text-cyan-400": isOpen,
                "text-gray-200": !isOpen
              })}>
                {title}
              </span>
            </div>
            <motion.div
              animate={{ 
                rotate: isOpen ? 180 : 0,
                color: isOpen ? '#22d3ee' : '#9ca3af'
              }}
              transition={{ duration: 0.3 }}
              className="group-hover:text-cyan-400 transition-colors"
            >
              <BsChevronDown size={14} />
            </motion.div>
          </motion.button>
        </div>
      )}

      <AnimatePresence initial={false}>
        {isOpen && !isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-4 pl-4 mt-1.5 flex flex-col gap-1 overflow-hidden border-l-2 border-cyan-500/30"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Sidebar Component - UPDATED with all necessary props
const Sidebar = ({ 
  isCollapsed, 
  setIsCollapsed,
  isMobile = false,
  isMobileMenuOpen = false,
  toggleMobileMenu 
}) => {
  const [openMenus, setOpenMenus] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navRef = useRef(null);
  const sidebarRef = useRef(null);
  const permissions = useSelector((state) => state.auth?.permissions || []);
  const location = useLocation();
  const user = useSelector((state) => state.auth?.user || {});
  
  // Flag to track initial mount
  const isInitialMount = useRef(true);
  // Flag to track if auto-expand has been done
  const autoExpanded = useRef(false);

  const hasPermission = (perm) => permissions.includes(perm);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Handle scroll for shadow effect
  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        setIsScrolled(navRef.current.scrollTop > 10);
      }
    };

    const navElement = navRef.current;
    if (navElement) {
      navElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (navElement) {
        navElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Auto-open menu based on current route - ONLY ONCE on initial load
  useEffect(() => {
    if (isInitialMount.current && !autoExpanded.current) {
      const path = location.pathname;
      const newOpenMenus = {};
      
      if (path !== "/dashboard" && path !== "/dashboard/park-dashboard") {
        if (path.includes("/dashboard/employee") || 
            path.includes("/dashboard/Employee-Park") ||
            path.includes("/dashboard/attendance") ||
            path.includes("/dashboard/Park-Attendance") ||
            path.includes("/dashboard/User-add") ||
            path.includes("/dashboard/User-add-bulk") ||
            path.includes("/dashboard/Attendance-Report") ||
            path.includes("/dashboard/User-Park-User-AddBulk") ||
            path.includes("/dashboard/Park-Attendance-Defaulters") || 
            path.includes("/dashboard/Project-wise-Defaulters")) {
          newOpenMenus.user = true;
        }
        
        if (path.includes("/dashboard/Park-Details") ||
            path.includes("/dashboard/User-Park-Admin-AddBulk") ||
            path.includes("/dashboard/view-ParkAdmin-User")) {
          newOpenMenus.park = true;
        }
        
        if (path.includes("/dashboard/task") || 
            path.includes("/dashboard/task-tracking") ||
            path.includes("/dashboard/Create-Tasks-Admin")) {
          newOpenMenus.task = true;
        }
        
        if (path.includes("/dashboard/leaves") || 
            path.includes("/dashboard/Apply-Leave") ||
            path.includes("/dashboard/view-leave-types") ||
            path.includes("/dashboard/Create-Leavemaster-Type") ||
            path.includes("/dashboard/create-leave-type") ||
            path.includes("/dashboard/leaveTypesPolicies")) {
          newOpenMenus.leave = true;
        }
        
        if (path.includes("/dashboard/View-districts") || 
            path.includes("/dashboard/Create-Districts") ||
            path.includes("/dashboard/View-Municipalitie") ||
            path.includes("/dashboard/Create-Municipality") ||
            path.includes("/dashboard/View-Admin") ||
            path.includes("/dashboard/Create-Admin")) {
          newOpenMenus.ulb = true;
        }
        
        if (path.includes("/dashboard/task-title") || 
            path.includes("/dashboard/create-task-title") ||
            path.includes("/dashboard/view-holiday") ||
            path.includes("/dashboard/create-holiday") ||
            path.includes("/dashboard/visit-Location") ||
            path.includes("/dashboard/Create-Visit-Location") ||
            path.includes("/dashboard/view-Designations") ||
            path.includes("/dashboard/create-Designation")) {
          newOpenMenus.setting = true;
        }
        
        if (path.includes("/dashboard/create-role") || 
            path.includes("/dashboard/create-permission") ||
            path.includes("/dashboard/assign-permission")) {
          newOpenMenus.role = true;
        }
      }
      
      setOpenMenus(newOpenMenus);
      autoExpanded.current = true;
      isInitialMount.current = false;
    }
  }, [location.pathname]);

  // Manual toggle function
  const toggleMenu = (menu) => {
    setOpenMenus(prev => {
      const newState = { ...prev };
      newState[menu] = !prev[menu];
      return newState;
    });
  };

  // Reset auto-expand when user manually toggles
  const handleManualToggle = (menu) => {
    autoExpanded.current = true;
    toggleMenu(menu);
  };

  // Handle navigation click - close mobile menu
  const handleNavClick = () => {
    if (isMobile && toggleMobileMenu) {
      toggleMobileMenu();
    }
  };

  // Link classes
  const linkClass = (isActive) =>
    classNames(
      "flex items-center gap-3 font-medium py-3 px-3 rounded-xl transition-all duration-200 group relative border",
      { 
        "bg-gradient-to-r from-cyan-500/30 to-blue-500/30 border-cyan-400/50 shadow-lg shadow-cyan-500/20 text-white": isActive,
        "border-transparent text-gray-300 hover:bg-gradient-to-r hover:from-cyan-500/20 hover:to-blue-500/20 hover:border-cyan-500/20": !isActive 
      }
    );

  const collapsedLinkClass = (isActive) =>
    classNames(
      "flex items-center justify-center py-3 px-2 rounded-xl transition-all duration-200 group relative",
      { 
        "bg-gradient-to-r from-cyan-500/30 to-blue-500/30 shadow-lg shadow-cyan-500/20": isActive,
        "hover:bg-gradient-to-r hover:from-cyan-500/20 hover:to-blue-500/20": !isActive
      }
    );

  const subLinkClass = (isActive) =>
    classNames(
      "flex items-center gap-3 font-medium py-2.5 px-3 rounded-lg transition-all duration-200 group relative text-sm",
      { 
        "bg-gradient-to-r from-cyan-500/25 to-blue-500/25 border-l-2 border-cyan-400 shadow-md shadow-cyan-500/10 text-white": isActive,
        "border-l-2 border-transparent text-gray-300 hover:bg-gradient-to-r hover:from-cyan-500/15 hover:to-blue-500/15 hover:border-cyan-400/30": !isActive 
      }
    );

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'A';
  };

  // Get user role display name
  const getUserRole = () => {
    if (user?.role) {
      return user.role.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }
    return 'Super Admin';
  };

  // Determine if sidebar should be in collapsed mode
  const isSidebarCollapsed = isMobile ? true : isCollapsed;
  const sidebarWidth = isMobile 
    ? (isMobileMenuOpen ? "w-72" : "w-0")
    : (isCollapsed ? "w-20" : "w-72");

  return (
    <motion.div
      ref={sidebarRef}
      initial={{ x: isMobile ? -300 : 0 }}
      animate={{ 
        x: isMobile 
          ? (isMobileMenuOpen ? 0 : -300) 
          : 0 
      }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
      className={classNames(
        "bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900 h-full text-white flex flex-col shadow-2xl transition-all duration-300 ease-in-out overflow-hidden relative border-r border-gray-800/50 z-50",
        sidebarWidth
      )}
      style={{ 
        position: 'relative',
        minHeight: '100vh',
        height: '100%'
      }}
    >
      {/* Mobile Close Button */}
      {isMobile && isMobileMenuOpen && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          onClick={toggleMobileMenu}
          className="absolute top-4 right-4 p-2 hover:bg-gradient-to-r from-red-500/20 to-pink-500/20 rounded-lg transition-all duration-200 text-gray-400 hover:text-white z-50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </motion.button>
      )}

      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-blue-500/5 pointer-events-none"></div>
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(6,182,212,0.2)_1px,transparent_0)] bg-[length:20px_20px]"></div>
      </div>
      
      {/* Floating gradient orbs */}
      <div className="absolute top-20 -left-10 w-40 h-40 bg-cyan-500/10 rounded-full filter blur-3xl animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-20 -right-10 w-40 h-40 bg-blue-500/10 rounded-full filter blur-3xl animate-pulse pointer-events-none"></div>
      
      {/* Header */}
      <div className={classNames(
        "relative z-10 transition-all duration-300 border-b border-gray-800/50 backdrop-blur-sm bg-gray-900/50",
        {
          "p-5 pb-4": !isSidebarCollapsed && !isMobile,
          "p-4 pb-3": isSidebarCollapsed || isMobile
        }
      )}>
        <div className="flex items-center justify-between relative">
          {!isSidebarCollapsed && !isMobile ? (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <motion.div
                whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                <FiHome className="text-xl text-white relative z-10" />
              </motion.div>
              <div>
                <span className="font-bold text-xl tracking-wide bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  E-Attendance
                </span>
                <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
                  <span>Admin Panel</span>
                  <span className="w-1 h-1 bg-gray-500 rounded-full"></span>
                  <span className="text-cyan-400">{currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center w-full gap-3">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                <FiHome className="text-xl text-white relative z-10" />
              </motion.div>
              {!isMobile && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  className="p-2 hover:bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg transition-all duration-200 shadow-lg text-gray-300 hover:text-white bg-gray-800/80 backdrop-blur-sm border border-gray-700/50 w-8 h-8 flex items-center justify-center"
                  title="Expand"
                >
                  <AiOutlineMenuUnfold className="text-base" />
                </motion.button>
              )}
            </div>
          )}
          
          {/* Toggle Button - Desktop only */}
          {!isSidebarCollapsed && !isMobile && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 hover:bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg transition-all duration-200 shadow-lg text-gray-300 hover:text-white bg-gray-800/80 backdrop-blur-sm border border-gray-700/50 relative overflow-hidden group"
              title="Collapse"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/20 group-hover:to-blue-500/20 transition-all duration-300"></div>
              <AiOutlineMenuFold className="text-lg relative z-10" />
            </motion.button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav 
        ref={navRef}
        className={classNames(
          "flex flex-col gap-1 flex-1 overflow-y-auto relative z-10 transition-all duration-300",
          "custom-scrollbar",
          {
            "px-4 pb-6": !isSidebarCollapsed && !isMobile,
            "px-3 pb-4": isSidebarCollapsed || isMobile
          }
        )}
      >
        {/* Custom scrollbar styles */}
        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(6, 182, 212, 0.3);
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(6, 182, 212, 0.5);
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(6, 182, 212, 0.3) transparent;
          }
        `}</style>

        {/* Top shadow */}
        <motion.div 
          animate={{ opacity: isScrolled ? 1 : 0 }}
          className="sticky top-0 left-0 right-0 h-6 bg-gradient-to-b from-gray-900 via-gray-900 to-transparent pointer-events-none z-20"
        />

        {/* Bottom gradient */}
        <div className="sticky bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-gray-900 via-gray-900 to-transparent pointer-events-none z-20"></div>

        {/* Navigation Items */}
        <div className="relative space-y-1">
          {/* Dashboard Link */}
          {hasPermission("view_dashboard") && (
            <div className="mb-1">
              <MenuItemTooltip title="Dashboard" isCollapsed={isSidebarCollapsed}>
                <NavLink
                  to="/dashboard"
                  onClick={handleNavClick}
                  className={({ isActive }) => 
                    isSidebarCollapsed ? collapsedLinkClass(isActive) : linkClass(isActive)
                  }
                  onMouseEnter={() => setHoveredItem('dashboard')}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <div className="relative">
                    <motion.div
                      whileHover={{ rotate: [0, -10, 10, -5, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <RxDashboard className="text-xl" />
                    </motion.div>
                  </div>
                  {!isSidebarCollapsed && !isMobile && (
                    <span className="flex-1">
                      Dashboard
                    </span>
                  )}
                </NavLink>
              </MenuItemTooltip>
            </div>
          )}
          
          {/* Park Dashboard Link */}
          {hasPermission("view_park_dashboard") && (
            <div className="mb-1">
              <MenuItemTooltip title="Park Dashboard" isCollapsed={isSidebarCollapsed}>
                <NavLink
                  to="/dashboard/park-dashboard"
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    isSidebarCollapsed ? collapsedLinkClass(isActive) : linkClass(isActive)
                  }
                  onMouseEnter={() => setHoveredItem("park_dashboard")}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <div className="relative">
                    <motion.div
                      whileHover={{ rotate: [0, -10, 10, -5, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <RxDashboard className="text-xl" />
                    </motion.div>
                  </div>
                  {!isSidebarCollapsed && !isMobile && (
                    <span className="flex-1">
                      Park Dashboard
                    </span>
                  )}
                </NavLink>
              </MenuItemTooltip>
            </div>
          )}

          {/* User Management Group */}
          {(hasPermission("manage_users") || hasPermission("view_attendance")) && (
            <SidebarGroup
              title="User Management"
              icon={<GrUserManager className="text-xl" />}
              menuKey="user"
              isCollapsed={isSidebarCollapsed}
              openMenus={openMenus}
              toggleMenu={handleManualToggle}
              isMobile={isMobile}
            >
              {hasPermission("manage_municipality_users") && (
                <NavLink to="/dashboard/employee" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                  Employee
                </NavLink>
              )}
              {hasPermission("manage_park_users") && (
                <NavLink to="/dashboard/Employee-Park" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FaTree className="text-base" />
                  Park Employee
                </NavLink>
              )}
              {hasPermission("view_attendance") && (
                <NavLink to="/dashboard/attendance" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiCalendar className="text-base" />
                  Attendance
                </NavLink>
              )}
              {hasPermission("view_park_attendance") && (
                <NavLink to="/dashboard/Park-Attendance" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FaTree className="text-base" />
                  Park Attendance
                </NavLink>
              )}
              {hasPermission("view_park_defaultersAttendance") && (
                <NavLink to="/dashboard/Park-Attendance-Defaulters" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FaTree className="text-base" />
                  Park Attendance Defaulters
                </NavLink>
              )}
              {hasPermission("view_park_projectWiseAttendanceDefaulters") && (
                <NavLink to="/dashboard/Project-wise-Defaulters" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FaTree className="text-base" />
                  Project-wise Defaulters
                </NavLink>
              )}
              {hasPermission("User_add") && (
                <NavLink to="/dashboard/User-add" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                  User Add
                </NavLink>
              )}
              {hasPermission("User_Add_Bulk") && (
                <NavLink to="/dashboard/User-add-bulk" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <HiOutlineDocumentDuplicate className="text-base" />
                  Multiple User Add
                </NavLink>
              )}
              {hasPermission("view_attendance_report") && (
                <NavLink to="/dashboard/Attendance-Report" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <MdOutlineAttachMoney className="text-base" />
                  Attendance Report
                </NavLink>
              )}
              {hasPermission("Park_User_Bulk") && (
                <NavLink to="/dashboard/User-Park-User-AddBulk" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                  Upload Park User
                </NavLink>
              )}
              {hasPermission("create_park_user_Admin") && (
                <NavLink to="/dashboard/create-park-user-Admin" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                 Park User Add
                </NavLink>
              )}
               {hasPermission("get_Project_Attendance_Report") && (
                <NavLink to="/dashboard/get-Project-Attendance-Report" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                 Park Attendance Report
                </NavLink>
              )}
              {hasPermission("view_attendance") && (
                <NavLink to="/dashboard/employee-detailed-attendance" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                 Employee Attendance Report
                </NavLink>
              )}
            </SidebarGroup>
          )}

          {/* Park Management Group */}
          {hasPermission("manage_park") && (
            <SidebarGroup
              title="Park Management"
              icon={<FaTree className="text-xl" />}
              menuKey="park"
              isCollapsed={isSidebarCollapsed}
              openMenus={openMenus}
              toggleMenu={handleManualToggle}
              isMobile={isMobile}
            >
              {hasPermission("Park_Admin_Bulk") && (
                <NavLink to="/dashboard/User-Park-Admin-AddBulk" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                  Upload Park Admin
                </NavLink>
              )}
              {hasPermission("view_park_Admin") && (
                <NavLink to="/dashboard/view-ParkAdmin-User" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiShield className="text-base" />
                  Park Admin
                </NavLink>
              )}
              {hasPermission("view_park_details_Admin") && (
                <NavLink to="/dashboard/Park-Details" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FaTree className="text-base" />
                  Park Details
                </NavLink>
              )}
            </SidebarGroup>
          )}

          {/* Task Management Group */}
          {hasPermission("view_tasks") && (
            <SidebarGroup
              title="Task Management"
              icon={<GrTask className="text-xl" />}
              menuKey="task"
              isCollapsed={isSidebarCollapsed}
              openMenus={openMenus}
              toggleMenu={handleManualToggle}
              isMobile={isMobile}
            >
              {hasPermission("manage_tasks") && (
                <NavLink to="/dashboard/task" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <BiTask className="text-base" />
                  Task Details
                </NavLink>
              )}
              {hasPermission("view_tasks_track") && (
                <NavLink to="/dashboard/task-tracking" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <TbMapPin className="text-base" />
                  Task Tracking
                </NavLink>
              )}
              {hasPermission("Create_tasks_admin") && (
                <NavLink to="/dashboard/Create-Tasks-Admin" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create Tasks
                </NavLink>
              )}
              {hasPermission("track_your_agent") && (
                <NavLink to="/dashboard/track-your-agent" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <TbMapPin className="text-base" />
                  Track Your Agent
                </NavLink>
              )}
              
            </SidebarGroup>
          )}

          {/* Leaves Management Group */}
          {hasPermission("view_leaves") && (
            <SidebarGroup
              title="Leaves Management"
              icon={<IoLeafOutline className="text-xl" />}
              menuKey="leave"
              isCollapsed={isSidebarCollapsed}
              openMenus={openMenus}
              toggleMenu={handleManualToggle}
              isMobile={isMobile}
            >
              {hasPermission("view_leaves") && (
                <NavLink to="/dashboard/leaves" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <IoLeafOutline className="text-base" />
                  Leave Details
                </NavLink>
              )}
              {hasPermission("leaves_apply") && (
                <NavLink to="/dashboard/Apply-Leave" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiCalendar className="text-base" />
                  Apply leave
                </NavLink>
              )}
              {hasPermission("view_leave_types") && (
                <NavLink to="/dashboard/view-leave-types" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <IoLeafOutline className="text-base" />
                  Leave Types
                </NavLink>
              )}
              {hasPermission("create_leave_types") && (
                <NavLink to="/dashboard/Create-Leavemaster-Type" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create Leave Types
                </NavLink>
              )}
              {hasPermission("update_leave_types") && (
                <NavLink to="/dashboard/create-leave-type" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                  Assign Leave
                </NavLink>
              )}
              {hasPermission("emp_to_user_leave_types") && (
                <NavLink to="/dashboard/leaveTypesPolicies" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiShield className="text-base" />
                  Leave Policies
                </NavLink>
              )}
            </SidebarGroup>
          )}

          {/* ULB Management Group */}
          {hasPermission("districts_view") && (
            <SidebarGroup
              title="ULB Management"
              icon={<FaBuilding className="text-xl" />}
              menuKey="ulb"
              isCollapsed={isSidebarCollapsed}
              openMenus={openMenus}
              toggleMenu={handleManualToggle}
              isMobile={isMobile}
            >
              {hasPermission("districts_view") && (
                <NavLink to="/dashboard/View-districts" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FaBuilding className="text-base" />
                  District Details
                </NavLink>
              )}
              {hasPermission("districts_create") && (
                <NavLink to="/dashboard/Create-Districts" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create District
                </NavLink>
              )}
              {hasPermission("municipalities_view") && (
                <NavLink to="/dashboard/View-Municipalitie" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FaBuilding className="text-base" />
                  Municipalities
                </NavLink>
              )}
              {hasPermission("municipalities_create") && (
                <NavLink to="/dashboard/Create-Municipality" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create Municipality
                </NavLink>
              )}
              {hasPermission("municipalities_admin_view") && (
                <NavLink to="/dashboard/View-Admin" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiShield className="text-base" />
                  ULB Admin
                </NavLink>
              )}
              {hasPermission("municipalities_admin_create") && (
                <NavLink to="/dashboard/Create-Admin" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create ULB Admin
                </NavLink>
              )}
            </SidebarGroup>
          )}

          {/* Settings Group */}
          {(hasPermission("view_tasks_title") || hasPermission("create_tasks_title") || 
            hasPermission("view_holiday") || hasPermission("create_holiday") ||
            hasPermission("view_locations_Dropdown") || hasPermission("create_locations_Dropdown") ||
            hasPermission("designation_view") || hasPermission("designation_create")) && (
            <SidebarGroup
              title="Settings"
              icon={<FiSettings className="text-xl" />}
              menuKey="setting"
              isCollapsed={isSidebarCollapsed}
              openMenus={openMenus}
              toggleMenu={handleManualToggle}
              isMobile={isMobile}
            >
              {hasPermission("view_tasks_title") && (
                <NavLink to="/dashboard/task-title" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <BiTask className="text-base" />
                  Task Titles
                </NavLink>
              )}
              {hasPermission("create_tasks_title") && (
                <NavLink to="/dashboard/create-task-title" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create Task Title
                </NavLink>
              )}
              {hasPermission("view_holiday") && (
                <NavLink to="/dashboard/view-holiday" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiCalendar className="text-base" />
                  Holidays
                </NavLink>
              )}
              {hasPermission("create_holiday") && (
                <NavLink to="/dashboard/create-holiday" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create Holiday
                </NavLink>
              )}
              {hasPermission("view_locations_Dropdown") && (
                <NavLink to="/dashboard/visit-Location" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <TbMapPin className="text-base" />
                  Visit Location
                </NavLink>
              )}
              {hasPermission("create_locations_Dropdown") && (
                <NavLink to="/dashboard/Create-Visit-Location" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create Visit Location
                </NavLink>
              )}
              {hasPermission("designation_view") && (
                <NavLink to="/dashboard/view-Designations" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                  Designations
                </NavLink>
              )}
              {hasPermission("designation_create") && (
                <NavLink to="/dashboard/create-Designation" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create Designation
                </NavLink>
              )}
              {hasPermission("view_flash_message") && (
                <NavLink to="/dashboard/Flash_Message" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Flash Messages
                </NavLink>
              )}
            </SidebarGroup>
          )}

          {/* Role Management Group */}
          {(hasPermission("create_role") || hasPermission("create_permission") || 
            hasPermission("assign_permission_to_role")) && (
            <SidebarGroup
              title="Role Management"
              icon={<FiShield className="text-xl" />}
              menuKey="role"
              isCollapsed={isSidebarCollapsed}
              openMenus={openMenus}
              toggleMenu={handleManualToggle}
              isMobile={isMobile}
            >
              {hasPermission("create_role") && (
                <NavLink to="/dashboard/create-role" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiShield className="text-base" />
                  Create Role
                </NavLink>
              )}
              {hasPermission("create_permission") && (
                <NavLink to="/dashboard/create-permission" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiSettings className="text-base" />
                  Create Permission
                </NavLink>
              )}
              {hasPermission("assign_permission_to_role") && (
                <NavLink to="/dashboard/assign-permission" onClick={handleNavClick} className={({ isActive }) => subLinkClass(isActive)}>
                  <FiUsers className="text-base" />
                  Assign Permission
                </NavLink>
              )}
            </SidebarGroup>
          )}

          {/* Geofence Links */}
          {hasPermission("view_geofence") && (
            <MenuItemTooltip title="Geofence" isCollapsed={isSidebarCollapsed}>
              <NavLink
                to="/dashboard/geofence"
                onClick={handleNavClick}
                className={({ isActive }) => 
                  isSidebarCollapsed ? collapsedLinkClass(isActive) : linkClass(isActive)
                }
              >
                <motion.div whileHover={{ rotate: [0, -10, 10, -5, 0] }} transition={{ duration: 0.5 }}>
                  <IoGitMergeOutline className="text-xl" />
                </motion.div>
                {!isSidebarCollapsed && !isMobile && "Geofence"}
              </NavLink>
            </MenuItemTooltip>
          )}

          {hasPermission("view_park_geofence") && (
            <MenuItemTooltip title="Park Geofence" isCollapsed={isSidebarCollapsed}>
              <NavLink
                to="/dashboard/parkGeofence"
                onClick={handleNavClick}
                className={({ isActive }) => 
                  isSidebarCollapsed ? collapsedLinkClass(isActive) : linkClass(isActive)
                }
              >
                <motion.div whileHover={{ rotate: [0, -10, 10, -5, 0] }} transition={{ duration: 0.5 }}>
                  <IoGitMergeOutline className="text-xl" />
                </motion.div>
                {!isSidebarCollapsed && !isMobile && "Park Geofence"}
              </NavLink>
            </MenuItemTooltip>
          )}

          {/* Spacer */}
          <div className="h-4"></div>

          {/* Logout Button */}
          <div className="mt-2 pt-4 border-t border-gray-800/50">
            <MenuItemTooltip title="Logout" isCollapsed={isSidebarCollapsed}>
              <NavLink
                to="/logout"
                onClick={handleNavClick}
                className={({ isActive }) => 
                  isSidebarCollapsed ? collapsedLinkClass(isActive) : 
                  "flex items-center gap-3 font-medium py-3 px-3 rounded-xl transition-all duration-200 group border border-transparent hover:bg-gradient-to-r from-red-500/20 to-pink-500/20 hover:border-red-500/20 text-gray-300 hover:text-white"
                }
              >
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, -5, 0], scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                >
                  <FiLogOut className="text-xl" />
                </motion.div>
                {!isSidebarCollapsed && !isMobile && (
                  <span className="flex-1">
                    Logout
                  </span>
                )}
              </NavLink>
            </MenuItemTooltip>
          </div>
        </div>
      </nav>

      {/* User Profile Section - Hide on mobile */}
      {!isSidebarCollapsed && !isMobile && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="px-4 pb-4 border-t border-gray-800/50 relative z-10 bg-gradient-to-t from-gray-900 via-gray-900 to-transparent"
        >
          <div className="flex items-center gap-3 pt-4">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="relative"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                <span className="font-bold text-white relative z-10">{getUserInitials()}</span>
              </div>
              <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute -bottom-1 -right-1 h-3 w-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full border-2 border-gray-900"
              />
            </motion.div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-white truncate">
                {user?.name || 'Admin User'}
              </div>
              <div className="text-xs text-gray-400 truncate flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span>
                {getUserRole()}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Sidebar;